import { TileCropPlanner } from '@telespot/domain';
import { Coordinates } from '../../entities';
import { ImageDimensions } from '../roi-size-service';

/**
 * Where an ROI sits inside its own crop, as percentages of the crop's side.
 * Ready to hand to CSS - the crop is served square, so both axes divide by the
 * same side.
 */
export interface RoiCropBox {
  leftPercent: number;
  topPercent: number;
  widthPercent: number;
  heightPercent: number;
}

/**
 * Domain service for placing an ROI inside the crop that was cut around it.
 *
 * A crop is a padded square centred on the ROI, so how much of it the ROI
 * covers depends on the source image's pixel dimensions: below the crop side an
 * ROI is a small patch in the middle, above it a constant 1/(1 + 2·padding) of
 * the frame. Reproduces the geometry {@link TileCropPlanner} cuts with, since
 * the crop arrives as bare pixels with no record of the region it came from.
 */
export class CropOverlayService {
  /**
   * Places an ROI inside its crop.
   *
   * A point of interest carries no extent, and comes back as a box of zero size at the
   * point's own place in the crop - which is its centre, since the crop is cut centred on
   * the region. Marking that spot is the renderer's business; inventing extent here would
   * report a region the finding never claimed.
   *
   * @param coordinates - Normalized (0-1) roi coordinates
   * @param image - Source image dimensions in pixels
   * @returns The {@link RoiCropBox}, or undefined when there is nothing to place -
   * coordinates out of bounds or reversed, or an image whose dimensions are unknown
   */
  public static computeRoiBox(coordinates: Coordinates, image: ImageDimensions): RoiCropBox | undefined {
    if (!this.isPlaceable(coordinates) || !this.hasPixelSize(image)) return undefined;

    const box = TileCropPlanner.cropBox(coordinates, image);

    const x0 = Math.floor(coordinates.x0 * image.width);
    const x1 = Math.floor(coordinates.x1 * image.width);
    const y0 = Math.floor(coordinates.y0 * image.height);
    const y1 = Math.floor(coordinates.y1 * image.height);

    const boxWidth = box.x1 - box.x0;
    const boxHeight = box.y1 - box.y0;

    // The crop is fitted to a square, so its longest side sets the scale and the
    // other one is centred - letterboxing the oblong box a clamp at an image
    // edge produces. Percentages of that side, so the crop's pixel side never
    // enters the arithmetic.
    const side = Math.max(boxWidth, boxHeight);

    return {
      leftPercent: (((side - boxWidth) / 2 + x0 - box.x0) / side) * 100,
      topPercent: (((side - boxHeight) / 2 + y0 - box.y0) / side) * 100,
      widthPercent: ((x1 - x0) / side) * 100,
      heightPercent: ((y1 - y0) / side) * 100,
    };
  }

  /**
   * An in-bounds region whose minima do not exceed their maxima - the same bar
   * {@link TileCropPlanner.cropBox} sets, so it cannot reject what follows. Zero extent on
   * either axis passes: a point or a line has a place in the crop even without an area.
   */
  private static isPlaceable(coordinates: Coordinates): boolean {
    const values = [coordinates?.x0, coordinates?.x1, coordinates?.y0, coordinates?.y1];

    if (values.some((value) => !Number.isFinite(value) || value < 0 || value > 1)) return false;

    return coordinates.x0 <= coordinates.x1 && coordinates.y0 <= coordinates.y1;
  }

  private static hasPixelSize(image: ImageDimensions): boolean {
    return Number.isInteger(image?.width) && Number.isInteger(image?.height) && image.width > 0 && image.height > 0;
  }
}
